import { createAction, createAsyncThunk } from '@reduxjs/toolkit'
import { ThunkApi } from '../../store'
import { Group, GroupInit, ListResult } from 'common/api/v1/types'
import { ApplicationException } from '../../api/ApplicationException'
import { EnrichedGroup, GroupsRequestParams } from '../../api/nm-types'
import { enqueueErrorSnackbar, enqueueSuccessSnackbar } from './notificationActions'
import { withDefaultPagination } from '../../utils'

export const createGroup = createAsyncThunk<void, GroupInit, ThunkApi>(
  'group/createGroup',
  async (group, { dispatch, rejectWithValue, extra: { routes, api, navigate } }) => {
    try {
      await api.groupsApi.createGroup(group)
      navigate()(routes.groups())
      dispatch(enqueueSuccessSnackbar(`Created Group ${group.name}`))
    } catch (err) {
      dispatch(enqueueErrorSnackbar({ error: err, operation: 'create group' }))
      if (err instanceof ApplicationException) return rejectWithValue(err.errorInfo)
      throw err
    }
  },
)

export const removeGroup = createAsyncThunk<{ id: Group['id']; name: Group['name'] }, Group['id'], ThunkApi>(
  'groups/removeGroup',
  async (groupId, { dispatch, extra: { routes, api, navigate } }) => {
    try {
      const res = await api.groupsApi.removeGroup(groupId)
      navigate()(routes.groups())
      dispatch(enqueueSuccessSnackbar(`Removed Group: ${res.name}`))
      return res
    } catch (err) {
      dispatch(enqueueErrorSnackbar({ error: err, operation: 'delete group' }))
      throw err
    }
  },
)

export const updateGroup = createAsyncThunk<void, Group, ThunkApi>(
  'group/updateGroup',
  async (group, { dispatch, rejectWithValue, extra: { routes, api, navigate } }) => {
    try {
      await api.groupsApi.updateGroup(group.id, {
        name: group.name,
        applianceSecret: group.applianceSecret || '',
      })
      navigate()(routes.groups())
      dispatch(enqueueSuccessSnackbar(`Edited Group: ${group.name}`))
    } catch (err) {
      dispatch(enqueueErrorSnackbar({ error: err, operation: 'update group' }))
      if (err instanceof ApplicationException) return rejectWithValue(err.errorInfo)
      throw err
    }
  },
)

export const getGroups = createAsyncThunk<ListResult<EnrichedGroup>, Partial<GroupsRequestParams>, ThunkApi>(
  'group/getGroups',
  async (params, { dispatch, extra: { api } }) => {
    try {
      return await api.groupsApi.getPureGroups(withDefaultPagination(params))
    } catch (error) {
      dispatch(enqueueErrorSnackbar({ error, operation: 'get groups' }))
      throw error
    }
  },
)

export const getGroup = createAsyncThunk<Group, Group['id'], ThunkApi>(
  'group/getGroup',
  async (id, { dispatch, extra: { api } }) => {
    try {
      return await api.groupsApi.getGroup(id)
    } catch (error) {
      dispatch(enqueueErrorSnackbar({ error, operation: 'get group' }))
      throw error
    }
  },
)

export const removeGroups = createAsyncThunk<string[], Group['id'][], ThunkApi>(
  'groups/removeGroups',
  async (groupIds, { dispatch, extra: { api } }) => {
    try {
      for (const id of groupIds) {
        await api.groupsApi.removeGroup(id)
      }
      dispatch(enqueueSuccessSnackbar(`${groupIds.length} group${groupIds.length > 1 ? 's' : ''} removed`))
      return groupIds
    } catch (err) {
      dispatch(enqueueErrorSnackbar({ error: err, operation: 'delete groups' }))
      throw err
    }
  },
)

export const clearGroup = createAction('group/clearGroup')
export const clearGroups = createAction('group/clearGroups')
